// The pacing trace (client/tests/pacing.mjs; docs/platform/ClientShell.md "Pacing"): what the
// sim worker and the match screen did with every tick and every animation frame, in flat number
// arrays (one record = `stride` numbers). Off unless the page asks for it (`?trace` or the test
// hook `window.__wmdTrace`); the worker then also keeps every stepped tick's input, so the
// measured match can be replayed natively (`Sim::match_log` format, crates/sim/src/api.rs).

/** Milliseconds since the epoch at sub-millisecond resolution: comparable between the worker and
 * the page (each has its own `performance.timeOrigin`). */
export function wallMs(): number {
  return performance.timeOrigin + performance.now();
}

/** `steps` flags: LATE, run a tick period or more after it was due (a pass that ran several
 * ticks); FAST, a catch-up backlog's fast-forward tick (not presented). */
export const STEP_LATE = 1, STEP_MINE = 2, STEP_CHECKPOINT = 4, STEP_PRESENTED = 8, STEP_FAST = 16;
/** `steps` phase: the kind's code | the team << 8 (Lead). */
export const PHASE_CODES: Record<string, number> = { Boot: 0, Free: 1, Lead: 2, Wait: 3, Over: 4 };
/** `blocks` reasons. */
export const BLOCK_CODES: Record<string, number> = { Horizon: 1, Barrier: 2, Snapshot: 3, CatchUp: 4, Idle: 5, Done: 6 };

export type WorkerTrace = {
  /** Per stepped tick: tick, start (wallMs), sim step ms, checkpoint CRC ms, view ms (camera,
   * particles, frame and HUD views after the step), horizon - tick before the step, flags, the
   * phase after the step. */
  steps: number[];
  /** Per stop of the stepping: start, end (wallMs), reason (BLOCK_CODES), the tick it waited at. */
  blocks: number[];
  /** Per resume delay applied after a starvation: start (wallMs), delay ms. */
  holds: number[];
  /** Per pass whose wall time exceeded the 0.15 s clamp: wallMs, the dropped time in ms. */
  drops: number[];
  /** Per posted frame message: wallMs, its tick, the steps it carries, ms spent building it, land
   * bitmaps fetched for it. */
  posts: number[];
  /** Per pacing pass that did anything: start (wallMs), ms. */
  passes: number[];
  /** Per server `Frames` message: arrival (wallMs), through, released, frames carried. */
  frames: number[];
};

export function emptyWorkerTrace(): WorkerTrace {
  return { steps: [], blocks: [], holds: [], drops: [], posts: [], passes: [], frames: [] };
}

/** Numbers per `PageTrace.frames` record (pacing.mjs reads `strides.frames`). */
export const FRAME_STRIDE = 7;

export type PageTrace = {
  /** Per animation frame: rAF time (wallMs), the presented view's tick, steps presented (camera
   * views consumed), ms in the frame callback, ms of it in the renderer's draw, in the renderer's
   * update (scene from the view) and in the HUD's update. */
  frames: number[];
  /** Per worker message handled: wallMs, kind (1 frame, 2 land), ms. */
  messages: number[];
};
